import { toRecordStatus, type RecordStatus } from './recordStatus';

// Deliberately still `recordNotes` — renaming the key would orphan every note
// reviewers saved before statuses existed.
const KEY_PREFIX = 'larimar.aml.recordNotes.';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export type RecordNotes = Record<string, string>;
export type RecordStatuses = Record<string, RecordStatus>;

export interface RecordState {
  notes: RecordNotes;
  statuses: RecordStatuses;
}

interface StoredState extends RecordState {
  updatedAt: number;
}

function keyFor(amlReviewId: string): string {
  return `${KEY_PREFIX}${amlReviewId}`;
}

function asMap<T>(value: unknown): Record<string, T> | null {
  return value && typeof value === 'object' ? (value as Record<string, T>) : null;
}

function knownStatuses(value: unknown): RecordStatuses {
  return Object.fromEntries(
    Object.entries(asMap<unknown>(value) ?? {})
      .map(([recordId, status]) => [recordId, toRecordStatus(status)])
      .filter(([, status]) => status !== null)
  ) as RecordStatuses;
}

function parse(raw: string | null): StoredState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredState>;
    const notes = asMap<string>(parsed.notes);
    if (!notes && !asMap<unknown>(parsed.statuses)) return null;
    return {
      updatedAt: Number(parsed.updatedAt) || 0,
      notes: notes ?? {},
      statuses: knownStatuses(parsed.statuses),
    };
  } catch {
    return null;
  }
}

export function loadRecordState(amlReviewId: string): RecordState {
  try {
    const stored = parse(localStorage.getItem(keyFor(amlReviewId)));
    return { notes: stored?.notes ?? {}, statuses: stored?.statuses ?? {} };
  } catch {
    return { notes: {}, statuses: {} };
  }
}

export function saveRecordState(amlReviewId: string, state: RecordState): void {
  const notes = Object.fromEntries(
    Object.entries(state.notes).filter(([, note]) => note.trim() !== '')
  );
  const statuses = Object.fromEntries(
    Object.entries(state.statuses).filter(([, status]) => status !== 'unreviewed')
  );

  try {
    if (Object.keys(notes).length === 0 && Object.keys(statuses).length === 0) {
      localStorage.removeItem(keyFor(amlReviewId));
      return;
    }
    const stored: StoredState = { updatedAt: Date.now(), notes, statuses };
    localStorage.setItem(keyFor(amlReviewId), JSON.stringify(stored));
  } catch {
    // storage unavailable
  }
}

export function sweepRecordState(now: number = Date.now()): void {
  try {
    const stale = Object.keys(localStorage).filter((key) => {
      if (!key.startsWith(KEY_PREFIX)) return false;
      const parsed = parse(localStorage.getItem(key));
      return !parsed || now - parsed.updatedAt > MAX_AGE_MS;
    });
    for (const key of stale) localStorage.removeItem(key);
  } catch {
    // storage unavailable
  }
}
